// Transaction checklist — pure helpers for the per-record check-state model.
//
// Recreated from scratch under `src/entities/transactions/`. The legacy
// implementation lived at `src/pages/transactions/transactionChecklist.ts`
// and is the BEHAVIORAL reference (group ids, NA derivation rules, group +
// overall progress shapes). Test coverage in the co-located `.test.ts`
// is the contract.
//
// The shape:
//   - Every CHECK_GROUP is a labelled bucket of CheckKeys.
//   - `buildCheckItems(record, rule)` resolves the per-key `na` flag from
//     domain rules (annex_na, product waste-code, border type, transport
//     legs) plus the per-key `_required` switch on the matched `transaction_rules`
//     row. Items whose `_required` is `false` collapse to `na: true`.
//   - `computeGroupStates` rolls items up per group (NA items are dropped
//     from the denominator so a fully-NA group reads 100 %).
//   - `computeOverallProgress` rolls all NA-filtered items into a single
//     done/total/percent triple.

import type { TransactionRule } from "@shared/types/transaction";

// ── Keys ─────────────────────────────────────────────────────────────────

// Derived from the check registry (which also generates the DB views) —
// re-exported so existing imports from this module keep working.
import type { CheckKey } from "@shared/checks/checkRegistry";
export type { CheckKey };

export interface CheckItem {
  key: CheckKey;
  na: boolean;
  done: boolean;
}

// ── Groups ───────────────────────────────────────────────────────────────

export interface CheckGroup {
  id: "transport" | "admin";
  labelKey: string;
  keys: CheckKey[];
}

export const CHECK_GROUPS: CheckGroup[] = [
  {
    id: "transport",
    labelKey: "transactions.checklist.groups.transport",
    keys: [
      "transport_complete",
      "transport_order_generated",
      "transport_order_sent",
      "loading_notification_sent",
      "unloading_notification_sent",
      "unload_entry_request_sent",
      "unloading_confirmation_sent",
      "loading_photo_approved",
      "unload_weight_set",
      "number_of_bales_set",
    ],
  },
  {
    id: "admin",
    labelKey: "transactions.checklist.groups.admin",
    keys: [
      "annex7_generated",
      "cmr_generated",
      "recycling_process_set",
      "annex_copy_approved",
      "cmr_copy_approved",
      "weight_ticket_copy_approved",
      "annex_original_approved",
      "cmr_original_approved",
      "customs_docs_approved",
      "pending_docs_review",
      "unload_price_set",
      "load_price_set",
      "income_invoices_issued",
      "cost_invoices_issued",
      "load_invoices_issued",
      "unload_invoices_issued",
      "transport_invoices_issued",
    ],
  },
];

/** Flat list of all check keys across all groups, group-order preserving. */
export const ALL_CHECK_KEYS: CheckKey[] = CHECK_GROUPS.flatMap((g) => g.keys);

/** Checks that can NOT be manually overridden — derived from the registry
 *  (the same source that generates the DB views), re-exported here so all
 *  checklist metadata is consumed from one module. */
export { NON_OVERRIDABLE_CHECK_KEYS } from "@shared/checks/checkRegistry";


// ── Build items ──────────────────────────────────────────────────────────

type RecordLike = Partial<Record<CheckKey, boolean | string>> & {
  transport_order_na?: boolean | null;
  annex_na?: boolean | null;
  rule_id?: number | null;
  load_price?: number | null;
  unload_price?: number | null;
  border_type?: string | null;
  product?: { waste_code?: string | null } | null;
  transports?: { transport_type?: string | null; is_deleted?: boolean }[] | null;
  pending_docs_review_check?: string | null;
};

/** A rule field set to `false` collapses the corresponding check to NA. */
function ruleSkip(rule: TransactionRule | null, field: string): boolean {
  return (rule as Record<string, unknown> | null)?.[field] === false;
}

/** Resolve the NA flag for each check key given the transaction + matched rule. */
export function buildCheckItems(record: RecordLike, rule: TransactionRule | null): CheckItem[] {
  const transportOrderNa = !!record.transport_order_na;
  const annexNa = !!record.annex_na;
  const isNonEu = record.border_type === "non_eu";
  const hasWasteCode = !!record.product?.waste_code;
  const hasPositivePrice =
    (record.load_price != null && record.load_price > 0) ||
    (record.unload_price != null && record.unload_price > 0);
  const hasTruckLeg = (record.transports ?? []).some(
    (t) => !t.is_deleted && t.transport_type === "truck",
  );

  const naMap: Record<CheckKey, boolean> = {
    transport_complete: ruleSkip(rule, "transport_complete_required"),
    transport_order_generated: transportOrderNa || ruleSkip(rule, "transport_order_generated_required"),
    transport_order_sent: transportOrderNa || ruleSkip(rule, "transport_order_sent_required"),
    loading_notification_sent: ruleSkip(rule, "loading_notification_sent_required"),
    unloading_notification_sent: ruleSkip(rule, "unloading_notification_sent_required"),
    // Inverted polarity vs. the others — entry-request is opt-IN via the rule.
    unload_entry_request_sent: rule?.unload_entry_request_sent_required !== true,
    unloading_confirmation_sent: ruleSkip(rule, "unloading_confirmation_sent_required"),
    loading_photo_approved: ruleSkip(rule, "loading_photo_approved_required"),
    unload_weight_set: ruleSkip(rule, "unload_weight_set_required"),
    number_of_bales_set: !isNonEu || ruleSkip(rule, "number_of_bales_set_required"),
    annex7_generated: annexNa || !hasWasteCode || ruleSkip(rule, "annex7_generated_required"),
    cmr_generated: annexNa || !hasTruckLeg || ruleSkip(rule, "cmr_generated_required"),
    recycling_process_set: annexNa || !hasWasteCode || ruleSkip(rule, "recycling_process_set_required"),
    annex_copy_approved: annexNa || ruleSkip(rule, "annex_copy_approved_required"),
    // Copy checks mirror their ORIGINAL counterparts' NA rules: the CMR copy
    // needs a truck leg, the kwit-wagowy copy does not. Both are intra-EU only
    // (#305) — a transport leaving the EU is verified on its Aneks VII alone.
    cmr_copy_approved: annexNa || isNonEu || !hasTruckLeg || ruleSkip(rule, "cmr_copy_approved_required"),
    weight_ticket_copy_approved: annexNa || isNonEu || ruleSkip(rule, "weight_ticket_copy_approved_required"),
    annex_original_approved: annexNa || ruleSkip(rule, "annex_original_approved_required"),
    cmr_original_approved: annexNa || !hasTruckLeg || ruleSkip(rule, "cmr_original_approved_required"),
    customs_docs_approved: ruleSkip(rule, "customs_docs_approved_required"),
    pending_docs_review: false, // always relevant — comes from a DB view column
    unload_price_set: ruleSkip(rule, "unload_price_set_required"),
    load_price_set: ruleSkip(rule, "load_price_set_required"),
    income_invoices_issued: !hasPositivePrice || ruleSkip(rule, "income_invoices_issued_required"),
    cost_invoices_issued: !hasPositivePrice || ruleSkip(rule, "cost_invoices_issued_required"),
    load_invoices_issued: ruleSkip(rule, "load_invoices_issued_required"),
    unload_invoices_issued: ruleSkip(rule, "unload_invoices_issued_required"),
    transport_invoices_issued: ruleSkip(rule, "transport_invoices_issued_required"),
  };

  const pendingDocsDone = record.pending_docs_review_check === "completed";

  return ALL_CHECK_KEYS.map((key) => ({
    key,
    na: naMap[key],
    done: key === "pending_docs_review" ? pendingDocsDone : !!record[key],
  }));
}

// ── Group + overall state ────────────────────────────────────────────────

export interface GroupState {
  group: CheckGroup;
  checks: CheckItem[];
  done: number;
  total: number;
  percent: number;
}

/** Per-group rollup. NA items are dropped from both numerator and denominator. */
export function computeGroupStates(
  record: RecordLike,
  rule: TransactionRule | null,
): GroupState[] {
  const items = buildCheckItems(record, rule);
  const byKey = new Map(items.map((c) => [c.key, c]));

  return CHECK_GROUPS.map((group) => {
    const checks = group.keys
      .map((k) => byKey.get(k))
      .filter((c): c is CheckItem => !!c);
    const active = checks.filter((c) => !c.na);
    const done = active.filter((c) => c.done).length;
    const total = active.length;
    const percent = total > 0 ? Math.round((done / total) * 100) : 100;
    return { group, checks, done, total, percent };
  });
}

/** Cross-group rollup. Same NA-aware denominator as `computeGroupStates`. */
export function computeOverallProgress(
  record: RecordLike,
  rule: TransactionRule | null,
): { done: number; total: number; percent: number } {
  const items = buildCheckItems(record, rule);
  const active = items.filter((c) => !c.na);
  const done = active.filter((c) => c.done).length;
  const total = active.length;
  const percent = total > 0 ? Math.round((done / total) * 100) : 100;
  return { done, total, percent };
}
